import React, { useState, useEffect } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Box,
} from "@mui/material";
import { PiSortDescendingLight, PiSortAscendingLight } from "react-icons/pi";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { FaEdit, FaTrash } from "react-icons/fa";
import authService from "../../../utils/authService";
import { fetchAllTracksForCourses } from "../../../utils/trackUtils";


interface Data {
  subject_id?: string;
  subject_name: string;
  subject_alt_name: string;
  track: string;
  subject_description: string;
  del_row?: boolean;
  is_project?: boolean;
}

interface Track {
  track_id: string;
  track_name: string;
}

const Subject: React.FC = () => {
  const [subjects, setSubjects] = useState<Data[]>([]);  
  const [tracks, setTracks] = useState<Track[]>([]);
  
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalType, setModalType] = useState<string>("new");
  const [loading, setLoading] = useState<boolean>(false);
  
  const [formData, setFormData] = useState<Data>({
    subject_id: "",
    subject_name: "",
    subject_alt_name: "",
    track:"",
    subject_description: "",
    is_project: false
  });
  const [error, setError] = useState<string>("");
   const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [selectedSubject, setSelectedSubject] = useState<Data>()
  const [search, setSearch] = useState("");
  const [selectedTrack, setSelectedTrack] = useState('')
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const handleModal = (type: string, subject?: Data): void => {
    setError("")
    setModalType(type);
    if (type === "new") {
      setFormData({ subject_id: "", track: selectedTrack, subject_name: "", subject_alt_name: "" ,subject_description: "", is_project: false});
    } else if (subject) {
      setFormData(subject);
    }
    setShowModal(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };


  const handleSave = async (): Promise<void> => {
    setError("");
    const existingSubject = subjects.find(subject => (subject.subject_name.toLowerCase() === formData.subject_name.toLowerCase() && subject.subject_id?.toLowerCase() != formData.subject_id?.toLowerCase()));
  
    if (existingSubject) {
      setError("Subject Name not available");
      return;
    }
    if (formData.subject_name=='') {
      setError("Please enter a subject name");
      return;
    }
    if(formData.track ==""){
      setError("Please select a track");
      return;
    }
    let subjectsToSend = {...formData, by:sessionStorage.getItem("Email")};
    // setLoading(true);
    try {
      setLoading(true);
      const response = await authService.post('admin/content/create_subject/', subjectsToSend);
  
      if (response.status !== 200) {
        throw new Error('Failed to save subjects');
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('Error saving subjects:', error);
    }
  
    setShowModal(false);
    fetchSubjects();
  };
    useEffect(() => {
      fetchSubjects();
      fetchTracks();
     }, []);

     const fetchTracks = async () => {
      try {
        const data = await fetchAllTracksForCourses();
        setTracks(data);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
  
     const fetchSubjects = async () => {
       try {
         const response = await authService.get('admin/content/get_all_subjects/');
         if (response.status !== 200) {
           throw new Error('Failed to fetch subjects');
         }
         const data = await response.data;
         setSubjects(data.subjects);
         setLoading(false);  
       } catch (error) {
         setLoading(false);  
       }
     };

     const handleDelete = (subject: Data): void => {
      setSelectedSubject(subject);
      setShowDeleteModal(true);
    };
  
    const confirmDelete = async (): Promise<void> => {
      if (selectedSubject) {
        try {
          const json={
            subject_id: selectedSubject.subject_id
          }
          const response = await authService.post("admin/content/delete_subject/", json);
  
          if (response.status !== 200) {
            throw new Error("Failed to delete subject");
          }
          fetchSubjects();
          setShowDeleteModal(false);
        } catch (error) {
          console.error("Error deleting subject:", error);
        }
      }
    };

    const handleSort = (field: string) => {
      if (sortField === field) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortField(field);
        setSortDirection('asc');
      }
    };

    const getSortedSubjects = (): Data[] => {
      const filtered = subjects
        .filter((subject) => !subject.del_row && (subject.track === selectedTrack || selectedTrack === ''))
        .filter((subject) => subject.subject_name.toLowerCase().includes(search.toLowerCase()));

      if (!sortField) {
        return filtered;
      }

      return [...filtered].sort((a, b) => {
        let aValue: string | number | boolean;
        let bValue: string | number | boolean;

        switch (sortField) {
          case 'subject_name':
            aValue = a.subject_name.toLowerCase();
            bValue = b.subject_name.toLowerCase();
            break;
          case 'subject_alt_name':
            aValue = a.subject_alt_name.toLowerCase();
            bValue = b.subject_alt_name.toLowerCase();
            break;
          case 'subject_description':
            aValue = a.subject_description.toLowerCase();
            bValue = b.subject_description.toLowerCase();
            break;
          case 'is_project':
            aValue = a.is_project ? 1 : 0;
            bValue = b.is_project ? 1 : 0;
            break;
          default:
            return 0;
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortDirection === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        }

        return 0;
      });
    };

    const sortedSubjects = getSortedSubjects();

    
  return (
    <div className="border rounded-2 bg-white my-2 me-2" style={{ height: `calc(100vh - 90px)`, overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" p={2} style={{ flexShrink: 0 }} gap={2}>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
            <InputLabel id="subject-track-label">Tracks</InputLabel>
            <Select
              labelId="subject-track-label"
              value={selectedTrack}
              onChange={(e: SelectChangeEvent<string>) => setSelectedTrack(e.target.value)}
              label="Tracks"
              style={{ height: "36px" }}
            >
              <MenuItem value="">Select</MenuItem>
              {tracks.map((track) => (
                <MenuItem key={track.track_id} value={track.track_id}>
                  {track.track_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ minWidth: 200 }}
          />
        </Box>
        <button className="btn" onClick={() => { handleModal("new") }}>
          + New Subject
        </button>
      </Box>

      <TableContainer component={Paper} className="px-2" style={{ flexGrow: 1, overflow: "auto", borderRadius: 0 }}>
        <Table className="border border-2" size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell style={{ color: "#666" }} className="muifont mui-header-cell">
                Sl No
              </TableCell>
              <TableCell
                className="muifont mui-header-cell mui-sortable-header"
                onClick={() => handleSort('subject_name')}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <span style={{ color: "#666" }}>Subject Name</span>
                  <span style={{ marginLeft: "4px" }}>
                    {sortField === 'subject_name' ? (
                      sortDirection === 'asc' ? (
                        <PiSortDescendingLight size={16} />
                      ) : (
                        <PiSortAscendingLight size={16} />
                      )
                    ) : (
                      <HiOutlineDotsVertical size={16} />
                    )}
                  </span>
                </Box>
              </TableCell>
              <TableCell
                className="muifont mui-header-cell mui-sortable-header"
                onClick={() => handleSort('subject_alt_name')}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <span style={{ color: "#666" }}>Subject Alt Name</span>
                  <span style={{ marginLeft: "4px" }}>
                    {sortField === 'subject_alt_name' ? (
                      sortDirection === 'asc' ? (
                        <PiSortDescendingLight size={16} />
                      ) : (
                        <PiSortAscendingLight size={16} />
                      )
                    ) : (
                      <HiOutlineDotsVertical size={16} />
                    )}
                  </span>
                </Box>
              </TableCell>
              <TableCell
                className="muifont mui-header-cell mui-sortable-header"
                onClick={() => handleSort('subject_description')}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <span style={{ color: "#666" }}>Subject Description</span>
                  <span style={{ marginLeft: "4px" }}>
                    {sortField === 'subject_description' ? (
                      sortDirection === 'asc' ? (
                        <PiSortDescendingLight size={16} />
                      ) : (
                        <PiSortAscendingLight size={16} />
                      )
                    ) : (
                      <HiOutlineDotsVertical size={16} />
                    )}
                  </span>
                </Box>
              </TableCell>
              <TableCell
                className="muifont mui-header-cell mui-sortable-header"
                onClick={() => handleSort('is_project')}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <span style={{ color: "#666" }}>Is Project</span>
                  <span style={{ marginLeft: "4px" }}>
                    {sortField === 'is_project' ? (
                      sortDirection === 'asc' ? (
                        <PiSortDescendingLight size={16} />
                      ) : (
                        <PiSortAscendingLight size={16} />
                      )
                    ) : (
                      <HiOutlineDotsVertical size={16} />
                    )}
                  </span>
                </Box>
              </TableCell>
              <TableCell
                className="muifont mui-header-cell"
                style={{
                  borderRight: "none",
                }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedSubjects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" style={{ padding: "20px", color: "#666" }}>
                  No subjects found
                </TableCell>
              </TableRow>
            ) : (
              sortedSubjects.map((subject, index) => (
                <TableRow
                  key={index}
                  style={{
                    backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafafa",
                  }}
                  sx={{
                    "&:hover": {
                      backgroundColor: "#f0f0f0",
                    },
                  }}
                >
                  <TableCell className="muifont mui-body-cell">
                    {index + 1}
                  </TableCell>
                  <TableCell className="muifont mui-body-cell">
                    {subject.subject_name}
                  </TableCell>
                  <TableCell className="muifont mui-body-cell">
                    {subject.subject_alt_name}
                  </TableCell>
                  <TableCell className="muifont mui-body-cell">
                    {subject.subject_description}
                  </TableCell>
                  <TableCell className="muifont mui-body-cell">
                    {subject.is_project ? "Yes" : "No"}
                  </TableCell>
                  <TableCell
                    className="muifont mui-body-cell"
                    style={{
                      borderRight: "none",
                    }}
                  >
                    <FaEdit
                      className="fs-6 me-2"
                      role="button"
                      title="Edit"
                      onClick={() => { handleModal("edit", subject) }}
                      style={{ cursor: 'pointer' }}
                    />
                    <FaTrash
                      className="fs-6 me-2"
                      title="Delete"
                      role="button"
                      onClick={() => handleDelete(subject)}
                      style={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{modalType === "new" ? "Add New Subject" : "Edit Subject"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {error && <p style={{ color: "red" }}>{error}</p>}
            <Form.Group controlId="formTrackId" className="mt-3 d-flex align-items-center justify-content-between">
              <Form.Label>Track </Form.Label>
              <Form.Select
                name="track_id"
                value={formData.track}
                disabled={formData.subject_id!=''}
                onChange={(e) => { setFormData({ ...formData, track: e.target.value }) }}
                style={{
                  width: "50%",
                }}
                >
                <option value="">Select</option>
                {tracks.map((track) => (
                  <option key={track.track_id} value={track.track_id}>
                    {track.track_name}
                  </option>
                ))}
                </Form.Select>
            </Form.Group>
            <Form.Group controlId="formSubjectName" className="mt-3 d-flex align-items-center justify-content-between">
              <Form.Label>Subject Name</Form.Label>
              <Form.Control
                type="text"
                name="subject_name"
                value={formData.subject_name}
                onChange={handleChange}
                disabled={formData.subject_id !== ""}
                style={{
                  width: "50%"
                }}
                // placeholder="Enter Subject Name"
              />
            </Form.Group>

            <Form.Group controlId="formSubjectAltName" className="mt-3 d-flex align-items-center justify-content-between">
              <Form.Label>Subject Alt Name</Form.Label>
              <Form.Control
                type="text"
                name="subject_alt_name"
                value={formData.subject_alt_name}
                onChange={handleChange}
                // placeholder="Enter Alternate Subject Name"
                style={{
                  width: "50%"
                }}
              />
            </Form.Group>

            <Form.Group controlId="formSubjectDescription" className="mt-3 d-flex align-items-center justify-content-between">
              <Form.Label>Subject Description</Form.Label>
              <Form.Control
                type="text"
                name="subject_description"
                value={formData.subject_description}
                onChange={handleChange}
                // placeholder="Enter Subject Description"
                style={{
                  width: "50%"
                }}
              />
            </Form.Group>

            <Form.Group controlId="formIsProject" className="mt-3 d-flex align-items-center justify-content-between">
              <Form.Label>Is Project Subject?</Form.Label>
              <div style={{ width: "50%" }}>
                <Form.Check
                  type="checkbox"
                  id="is-project-checkbox"
                  name="is_project"
                  label="Yes"
                  checked={formData.is_project === true}
                  onChange={(e) => setFormData({ ...formData, is_project: e.target.checked })}
                />
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSave}>
            {loading ? <Spinner animation="border" size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
              <Modal.Header closeButton>
                <Modal.Title>Confirm Delete</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p>Are you sure you want to delete the subject: <strong>{selectedSubject?.subject_name}</strong>?</p>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                  No
                </Button>
                <Button variant="danger" onClick={confirmDelete}>
                  Yes, Delete
                </Button>
              </Modal.Footer>
            </Modal>
    </div>
  );
};

export default Subject;
